import React from "react";
import api from "../Auth/Api";
import { Box, Chip, IconButton, Typography, TextField, Dialog, DialogTitle, DialogContent, Button } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export const ATTACH_MAX_FILE = 5 * 1024 * 1024, ATTACH_MAX_TOTAL = 20 * 1024 * 1024;
const ALLOWED = ["image/png", "image/jpeg", "image/gif", "image/webp", "application/pdf", "text/plain", "text/markdown", "application/json", "text/csv"];
const COMPRESSIBLE = ["image/png", "image/jpeg"];   // re-encoded on the client when over the per-file limit
const mb = (n) => `${(n / 1048576).toFixed(1)} MB`;

// Turns a paste event / file list into pending attachments; returns the new list or an error string.
// Oversized PNG/JPEG files are kept (flagged tooBig) and compressed when the message is sent — the factor is
// chosen once every attachment is known (see uploadAll); other oversized types are refused here.
export function collectFiles(existing, fileList) {
    const files = Array.from(fileList || []).filter(f => f && f.size);
    const next = [...existing];
    for (const f of files) {
        const mime = f.type || "application/octet-stream";
        if (!ALLOWED.includes(mime)) return `"${f.name}": type ${mime} not allowed (images, PDF, text).`;
        const tooBig = f.size > ATTACH_MAX_FILE;
        if (tooBig && !COMPRESSIBLE.includes(mime)) return `"${f.name}" is ${mb(f.size)} — max 5 MB per file (only PNG/JPEG are compressed automatically).`;
        next.push({ file: f, name: f.name || `pasted-${Date.now()}.png`, mime, size: f.size, tooBig, preview: mime.startsWith("image/") ? URL.createObjectURL(f) : null });
    }
    // Budget check with the post-compression upper bound for images that will be shrunk.
    if (next.reduce((n, a) => n + Math.min(a.size, COMPRESSIBLE.includes(a.mime) ? ATTACH_MAX_FILE : Infinity), 0) > ATTACH_MAX_TOTAL) return "Attachments exceed 20 MB per message.";
    return next;
}

async function loadBitmap(file) {
    if (typeof createImageBitmap === "function") return createImageBitmap(file);
    return new Promise((resolve, reject) => {   // fallback: HTMLImageElement
        const url = URL.createObjectURL(file), img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("cannot decode " + file.name)); };
        img.src = url;
    });
}

// Re-encodes an image until it fits maxBytes: scale ladder (× `scale`) 1 → .85 → .7 → .55 → .4 → .3; at each rung a PNG
// tries PNG first (lossless) then JPEG q.85 / q.7, a JPEG tries q.85 / q.7. Returns a File (the transport sends File
// payloads as binary) with `originalSize`; throws when even the smallest rung does not fit.
export async function compressImage(file, maxBytes = ATTACH_MAX_FILE, scale = 1) {
    const bmp = await loadBitmap(file);
    const w = bmp.width || bmp.naturalWidth, h = bmp.height || bmp.naturalHeight;
    const canvas = document.createElement("canvas");
    const encode = (s, type, quality) => {
        canvas.width = Math.max(1, Math.round(w * s)); canvas.height = Math.max(1, Math.round(h * s));
        const ctx = canvas.getContext("2d");
        if (type === "image/jpeg") { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height); }   // no alpha in JPEG
        ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
        return new Promise(res => canvas.toBlob(res, type, quality));
    };
    const isPng = file.type === "image/png";
    const attempts = [];
    for (const rung of [1, 0.85, 0.7, 0.55, 0.4, 0.3]) {
        const s = Math.min(1, rung * scale);
        if (isPng) attempts.push([s, "image/png", undefined]);
        attempts.push([s, "image/jpeg", 0.85], [s, "image/jpeg", 0.7]);
    }
    let best = null;
    try {
        for (const [s, type, q] of attempts) {
            const blob = await encode(s, type, q);
            if (!blob) continue;
            if (!best || blob.size < best.size) best = blob;
            if (blob.size <= maxBytes) { best = blob; break; }
        }
    } finally { if (bmp.close) bmp.close(); }
    if (!best || best.size > maxBytes) throw new Error(`"${file.name}" cannot be compressed below ${mb(maxBytes)}.`);
    const name = best.type === "image/jpeg" ? file.name.replace(/\.(png|jpe?g)$/i, "") + ".jpg" : file.name;
    const out = new File([best], name, { type: best.type });
    out.originalSize = file.size;
    return out;
}

// Uploads every pending file through the backend (which stores it on the CDN); resolves to [{url,name,mime,size}].
// Compression happens here, once all attachments are known: pass 1 brings each oversized PNG/JPEG under 5 MB;
// pass 2 (up to twice) shrinks every image by a shared factor sqrt(20 MB / total) while the message total is over 20 MB.
// onProgress(text) receives a short status for the send button.
export async function uploadAll(pending, onProgress) {
    const items = pending.map(a => ({ ...a }));
    const isImg = a => COMPRESSIBLE.includes(a.mime);
    const total = () => items.reduce((n, a) => n + a.size, 0);
    const swap = (a, f) => { a.originalSize = a.originalSize || a.size; a.file = f; a.size = f.size; a.mime = f.type; a.name = f.name; a.tooBig = false; };
    for (const a of items) if (isImg(a) && a.size > ATTACH_MAX_FILE) {
        onProgress?.(`Compressing ${a.name}…`);
        swap(a, await compressImage(a.file, ATTACH_MAX_FILE));
    }
    for (let round = 0; round < 2 && total() > ATTACH_MAX_TOTAL; round++) {
        const imgs = items.filter(isImg);
        if (!imgs.length) break;
        const factor = Math.sqrt(ATTACH_MAX_TOTAL / total()) * 0.95;   // linear scale → area (≈ bytes) × factor²
        for (const a of imgs) {
            onProgress?.(`Shrinking ${a.name} (×${factor.toFixed(2)})…`);
            swap(a, await compressImage(a.file, ATTACH_MAX_FILE, factor));
        }
    }
    if (total() > ATTACH_MAX_TOTAL) throw new Error("Attachments exceed 20 MB even after compression — remove some.");
    const out = [];
    for (let i = 0; i < items.length; i++) {
        const a = items[i];
        if (a.size > ATTACH_MAX_FILE) throw new Error(`"${a.name}" is ${mb(a.size)} — max 5 MB per file.`);
        onProgress?.(`Uploading ${i + 1}/${items.length}…`);
        const r = await api.development("Uploading attachment", ["upload-attachment", encodeURIComponent(a.name), a.mime], { payload: a.file, silent: true });
        if (!r || !r.url) throw new Error(typeof r === "string" ? r : "upload failed");
        out.push(r);
    }
    return out;
}

// Frees the preview object URLs of pending attachments (call when a dialog drops its pending list: sent, closed, reset).
export function disposePending(list) {
    for (const a of list || []) if (a && a.preview) { try { URL.revokeObjectURL(a.preview); } catch { /* already revoked */ } }
}

// Pending-attachment strip under a composer: thumbnails/chips with remove + an attach button.
export function AttachmentStrip({ pending, onChange, onError }) {
    const inputRef = React.useRef();
    const add = (files) => { const r = collectFiles(pending, files); if (typeof r === "string") onError?.(r); else onChange(r); };
    const remove = (i) => { disposePending([pending[i]]); onChange(pending.filter((_, j) => j !== i)); };
    const big = pending.filter(a => a.tooBig).length;
    return (
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mt: 0.5 }}>
            <IconButton size="small" title="Attach a file (or paste an image into the text box)" onClick={() => inputRef.current?.click()}><AttachFileIcon fontSize="small" /></IconButton>
            <input ref={inputRef} type="file" multiple hidden accept={ALLOWED.join(",")} onChange={e => { add(e.target.files); e.target.value = ""; }} />
            {pending.map((a, i) => a.preview
                ? <Box key={i} sx={{ position: "relative", textAlign: "center" }}>
                    <img src={a.preview} alt={a.name} title={`${a.name} (${mb(a.size)})`} style={{ height: 56, borderRadius: 4, border: a.tooBig ? "1px solid #e69500" : "1px solid #ccc" }} />
                    <Chip size="small" label="×" onClick={() => remove(i)} sx={{ position: "absolute", top: -8, right: -8, height: 18 }} />
                    {a.tooBig && <Typography variant="caption" sx={{ display: "block", color: "warning.main", lineHeight: 1 }}>{mb(a.size)} → compressed</Typography>}
                  </Box>
                : <Chip key={i} size="small" color={a.tooBig ? "warning" : "default"} label={`${a.name} (${a.tooBig ? mb(a.size) + " → compressed" : Math.round(a.size / 1024) + " KB"})`} onDelete={() => remove(i)} />)}
            {pending.length > 0 && <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {pending.length} attachment{pending.length > 1 ? "s" : ""} — uploaded when you send{big ? `; ${big} image${big > 1 ? "s" : ""} compressed to fit 5 MB` : ""}
            </Typography>}
        </Box>
    );
}

// One composer field for every dialog that accepts attachments (briefing, messages, tasks, human review):
// a multiline TextField that takes pasted images + the pending strip + the error line. The owner keeps
// `pending` / `error` in its state and uploads with uploadAll() when it sends.
export function AttachableTextField({ value, onChange, pending, onPending, error, onError, label, placeholder, minRows = 4, autoFocus, sx }) {
    const setPending = (p) => { onPending(p); onError?.(""); };
    return (
        <>
            <TextField fullWidth multiline minRows={minRows} autoFocus={autoFocus} label={label} placeholder={placeholder} value={value}
                onChange={e => onChange(e.target.value)} sx={sx || { my: 1 }}
                onPaste={pasteHandler(pending, setPending, err => onError?.(err))} />
            <AttachmentStrip pending={pending} onChange={setPending} onError={err => onError?.(err)} />
            {error && <Typography variant="caption" color="error" sx={{ display: "block" }}>{error}</Typography>}
        </>
    );
}

// The backend appends "Attachments:" links to a body/description that carries attachments (for agents reading
// the plain text); the site renders the files itself, so it hides that trailer.
export const stripAttachmentLinks = (text, attachments) => attachments?.length ? (text || "").replace(/\n\nAttachments:\n(?:- [^\n]*: https?:\/\/\S+\n?)+$/, "") : (text || "");

// Paste handler for a TextField: images from the clipboard become attachments.
export const pasteHandler = (pending, onChange, onError) => (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return;
    e.preventDefault();
    const r = collectFiles(pending, files); if (typeof r === "string") onError?.(r); else onChange(r);
};

// ---------- attachment viewer: ONE popup for the whole site ----------
// Any component calls openAttachment(a, siblings) — the host (mounted once in PageLayout) shows the file in a
// dialog on the same page: images inline, PDFs and text/JSON/CSV in a frame (the CDN sends no CORS header, so a
// fetch would be blocked — a frame is not), anything else as a name + "open in new tab". With siblings the dialog
// pages through them (arrows / ← →).
const viewerListeners = new Set();
export function openAttachment(attachment, siblings) {
    const list = (siblings && siblings.length ? siblings : [attachment]).filter(a => a && a.url);
    if (!list.length) return;
    const index = Math.max(0, list.findIndex(a => a === attachment || (attachment && a.url === attachment.url)));
    for (const fn of viewerListeners) fn({ list, index });
}
const kindOf = (a) => {
    const mime = (a.mime || "").toLowerCase(), name = (a.name || a.url || "").toLowerCase();
    if (mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg)$/.test(name)) return "image";
    if (mime === "application/pdf" || /\.pdf$/.test(name)) return "pdf";
    if (mime.startsWith("text/") || mime === "application/json" || /\.(txt|md|json|csv|log)$/.test(name)) return "text";
    return "other";
};
export function AttachmentViewerHost() {
    const [state, setState] = React.useState(null);          // { list, index }
    React.useEffect(() => { viewerListeners.add(setState); return () => viewerListeners.delete(setState); }, []);
    const a = state ? state.list[state.index] : null, kind = a ? kindOf(a) : null;
    if (!a) return null;
    const n = state.list.length, go = (d) => setState(s => ({ ...s, index: (s.index + d + n) % n }));
    const close = () => setState(null);
    const onKey = (e) => { if (n > 1 && e.key === "ArrowLeft") go(-1); else if (n > 1 && e.key === "ArrowRight") go(1); };
    const size = a.size ? `${Math.round(a.size / 1024)} KB` : "";
    return (
        <Dialog open onClose={close} maxWidth="lg" fullWidth onKeyDown={onKey}>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, py: 1, pr: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={a.name}>{a.name || a.url}</Typography>
                    {(a.description || size) && <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>{[a.description, size].filter(Boolean).join(" · ")}</Typography>}
                </Box>
                {n > 1 && <>
                    <IconButton size="small" onClick={() => go(-1)} title="Previous (←)"><ChevronLeftIcon /></IconButton>
                    <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>{state.index + 1} / {n}</Typography>
                    <IconButton size="small" onClick={() => go(1)} title="Next (→)"><ChevronRightIcon /></IconButton>
                </>}
                <IconButton size="small" component="a" href={a.url} target="_blank" rel="noreferrer" title="Open in a new tab"><OpenInNewIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={close} title="Close (Esc)"><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: kind === "image" ? 1 : 2, bgcolor: kind === "image" ? "#222" : undefined, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                {kind === "image" && <img src={a.url} alt={a.name} style={{ maxWidth: "100%", maxHeight: "78vh", objectFit: "contain" }} />}
                {(kind === "pdf" || kind === "text") && <iframe src={a.url} title={a.name} style={{ width: "100%", height: "78vh", border: 0, background: "#fff" }} />}
                {kind === "other" && <Box sx={{ textAlign: "center" }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>No preview for this file type{a.mime ? ` (${a.mime})` : ""}.</Typography>
                    <Button variant="outlined" size="small" href={a.url} target="_blank" rel="noreferrer" startIcon={<OpenInNewIcon />}>Open in a new tab</Button>
                </Box>}
            </DialogContent>
        </Dialog>
    );
}

// Renders stored attachments (thumbnails for images, chips otherwise); clicking one opens the site-wide viewer
// popup (openAttachment) with the whole list to page through. Items may carry a `description` (simulator
// uploads): it becomes the hover title. `caption(a)` adds a text line under each item; `height` sizes thumbnails.
export function AttachmentList({ attachments, caption, height = 64 }) {
    if (!attachments || !attachments.length) return null;
    return (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5, alignItems: "flex-start" }}>
            {attachments.map((a, i) => {
                const title = a.description || a.name;
                const open = a.url ? () => openAttachment(a, attachments) : undefined;
                const item = kindOf(a) === "image" && a.url
                    ? <img src={a.url} alt={a.name} title={title} onClick={open} style={{ height, borderRadius: 4, border: "1px solid #ccc", cursor: "pointer" }} />
                    : <Chip size="small" clickable={!!open} onClick={open} title={title} color={a.error ? "error" : "default"}
                        label={`${a.name}${a.error ? " — upload failed" : ` (${Math.round((a.size || 0) / 1024)} KB)`}`} />;
                if (!caption) return <React.Fragment key={i}>{item}</React.Fragment>;
                return (
                    <Box key={i} sx={{ maxWidth: 260 }}>
                        {item}
                        <Typography variant="caption" sx={{ display: "block", color: "text.secondary", lineHeight: 1.2 }}>{caption(a)}</Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

if (process.env.NODE_ENV !== "production") window.__aidevCompress = compressImage;   // dev hook for verification
